/* global runtime, mergeState, safeArray, num, nbToIso, isoToNb, deriveAccountingLedgerVouchers, deriveVat, derivePayroll, dashboardSupplierInvoicePaymentSummary, invoicePaymentSummary, isActiveExpense */
// HOVEDBOKSLINJER PER KONTO OG PERIODE
//
// Avstemmingens fagskjermer trenger å kunne se BAK saldoen: hvilke posteringer
// som faktisk utgjør bevegelsen på en konto i en måned. Saldobalansen svarer
// ikke på det — `buildTrialTotalsForRange` aggregerer posteringene til tall og
// kaster linjene.
//
// Linjene finnes likevel. `deriveAccountingLedgerVouchers` bygger dem allerede
// for SAF-T og komplett regnskapseksport, med hendelsens egen dato og en
// kildereferanse per bilag. Denne fila er et UTVALG over dem, ikke en ny
// innsamler: en egen innsamler ville vært et andre sted å bestemme hva som er
// bokført, og de to ville drevet fra hverandre første gang noen la til en ny
// bilagstype.
//
// Read-only. Ingen skriving, ingen Firebase.

const ledgerLineIsoDate = (voucher) => {
  const value = String(voucher?.date || '');
  // Bilagene bærer norsk dato i UI-form. `nbToIso` er den samme konverteringen
  // resten av regnskapet bruker.
  return value.includes('.') ? nbToIso(value) : value.slice(0, 10);
};

// Alle hovedbokslinjer i et datointervall, eventuelt avgrenset til bestemte
// kontoer. Kontokoden kommer fra posteringen selv — den utledes ikke av
// beløpets fortegn eller av bilagstypen.
const buildLedgerLines = ({ from, to, accountCodes, state: suppliedState } = {}) => {
  const state = suppliedState || mergeState(runtime.appState);
  const wanted = accountCodes && accountCodes.length ? new Set(accountCodes.map((code) => String(code).trim())) : null;
  const fromIso = String(from || '');
  const toIso = String(to || '');
  const lines = [];

  for (const voucher of deriveAccountingLedgerVouchers(state)) {
    const date = ledgerLineIsoDate(voucher);
    if (!date) continue;
    if (fromIso && date < fromIso) continue;
    if (toIso && date > toIso) continue;
    for (const posting of safeArray(voucher.postings)) {
      const accountCode = String(posting?.nr || posting?.accountCode || '').trim();
      if (!accountCode) continue;
      if (wanted && !wanted.has(accountCode)) continue;
      const amount = num(posting?.amount ?? posting?.signedAmount);
      if (!amount) continue;
      lines.push({
        id: `${voucher.sourceType || 'bilag'}:${voucher.id}:${accountCode}:${lines.length}`,
        date,
        accountCode,
        accountName: posting?.name || '',
        description: posting?.desc || posting?.description || voucher.name || '',
        amount,
        voucherId: voucher.id,
        voucherNumber: voucher.num || '',
        voucherName: voucher.name || '',
        sourceType: voucher.sourceType || '',
        sourceId: voucher.sourceId || voucher.id,
        kind: voucher.kind || '',
        status: voucher.status || 'active',
      });
    }
  }
  return lines.sort((a, b) => a.date.localeCompare(b.date) || String(a.voucherNumber).localeCompare(String(b.voucherNumber)));
};

// Bevegelsen per konto i intervallet. Brukes til å kontrollere at linjene
// stemmer med saldobalansen — de skal, siden begge kommer fra de samme
// posteringsfunksjonene.
const ledgerLineTotals = (lines = []) => {
  const totals = new Map();
  for (const line of safeArray(lines)) {
    totals.set(line.accountCode, Math.round(((totals.get(line.accountCode) || 0) + num(line.amount)) * 100) / 100);
  }
  return totals;
};

// Hovedbokslinjer gruppert per konto, med sum. Det fagskjermene faktisk vil ha:
// «hva består bevegelsen på 1500 av i august».
const buildLedgerLinesByAccount = ({ from, to, accountCodes, state } = {}) => {
  const lines = buildLedgerLines({ from, to, accountCodes, state });
  const byAccount = new Map();
  for (const line of lines) {
    if (!byAccount.has(line.accountCode)) byAccount.set(line.accountCode, { accountCode: line.accountCode, accountName: line.accountName, lines: [], total: 0 });
    const entry = byAccount.get(line.accountCode);
    entry.lines.push(line);
    entry.total = Math.round((entry.total + line.amount) * 100) / 100;
    if (!entry.accountName && line.accountName) entry.accountName = line.accountName;
  }
  return [...byAccount.values()].sort((a, b) => a.accountCode.localeCompare(b.accountCode, 'nb'));
};

// Åpne poster på en reskontrokonto: postene som ikke er gjort opp av en
// motpostering på samme konto i perioden. Dette er en ALDERSVISNING av
// hovedboken, ikke en reskontro med kunde- og leverandørkobling — den siste
// krever partsinformasjon som ikke ligger på posteringslinjen.
const ledgerLineAgeBucket = (date, referenceDate) => {
  const from = new Date(String(date) + 'T00:00:00');
  const to = new Date(String(referenceDate) + 'T00:00:00');
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 'Uten dato';
  const days = Math.round((to - from) / 86400000);
  if (days <= 30) return '0–30 dager';
  if (days <= 60) return '31–60 dager';
  if (days <= 90) return '61–90 dager';
  return 'Over 90 dager';
};


// ------------------------------------------------------------------
// Fagtall for avstemmingsområdene
// ------------------------------------------------------------------
// MVA og lønn har egne modeller i appen. Avstemmingen leser dem — den regner
// ikke ut avgiften eller lønnen på nytt. To steder å regne ut den samme
// avgiften ville gitt to svar, og avstemmingen ville avstemt mot seg selv.

const avstPeriodMonths = (periodId) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return null;
  return { year, monthIndex };
};

const avstVatFacts = ({ periodId, state: suppliedState } = {}) => {
  const period = avstPeriodMonths(periodId);
  if (!period) return null;
  const state = suppliedState || mergeState(runtime.appState);
  // Avgrenset til maneden avstemmingen gjelder. MVA-terminen er som regel to
  // maneder, men avstemmingen gjores per periode — og en termin som spenner over
  // to avstemte maneder skal stemme i begge.
  return deriveVat(state, period.year, { months: [period.monthIndex, period.monthIndex] });
};

const avstPayrollFacts = ({ periodId, state: suppliedState } = {}) => {
  const state = suppliedState || mergeState(runtime.appState);
  const payroll = derivePayroll(state);
  const runs = safeArray(payroll?.runs).filter((run) => String(run?.period || '') === String(periodId));
  return { ...payroll, runs };
};


// ------------------------------------------------------------------
// Åpne poster i reskontroen
// ------------------------------------------------------------------
// Både salgsfakturaer og leverandørfakturaer bærer forfallsdato og et restbeløp
// etter betalinger. Restbeløpet regnes ikke ut her: salgssiden har det på
// fakturaen (`remainingAmount`), kjøpssiden i `dashboardSupplierInvoicePaymentSummary`.
// To steder å regne ut hva som er betalt ville gitt to svar på hvor mye kunden
// skylder.
//
// Alderen regnes fra FORFALLSDATOEN. En faktura med 60 dagers kreditt er ikke
// forfalt fordi den er 45 dager gammel.

const avstOpenItemDate = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;
  const iso = text.includes('.') ? text.split('.').reverse().join('-') : text.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;
};

const AVST_OPEN_ITEM_BUCKETS = ['Ikke forfalt', '0–30 dager', '31–60 dager', '61–90 dager', 'Over 90 dager', 'Uten forfallsdato'];

const avstOpenItemBucket = (dueDate, referenceDate) => {
  const due = avstOpenItemDate(dueDate);
  const reference = avstOpenItemDate(referenceDate);
  if (!due || !reference) return 'Uten forfallsdato';
  const days = Math.round((new Date(reference + 'T00:00:00') - new Date(due + 'T00:00:00')) / 86400000);
  if (days < 0) return 'Ikke forfalt';
  if (days <= 30) return '0–30 dager';
  if (days <= 60) return '31–60 dager';
  if (days <= 90) return '61–90 dager';
  return 'Over 90 dager';
};

// Leverandørfakturaer med noe utestående på referansedatoen. Fakturaer utstedt
// etter perioden hører ikke til denne avstemmingen.
const avstSupplierOpenItems = ({ referenceDate, state: suppliedState } = {}) => {
  const state = suppliedState || mergeState(runtime.appState);
  const rows = [];
  for (const expense of safeArray(state.expenses)) {
    if (!expense?.isSupplierInvoice) continue;
    if (expense.status === 'annulled') continue;
    const issued = avstOpenItemDate(expense.date);
    if (issued && referenceDate && issued > referenceDate) continue;
    const summary = dashboardSupplierInvoicePaymentSummary(state, expense);
    const remaining = Math.round(num(summary?.remainingAmount) * 100) / 100;
    if (Math.abs(remaining) < 0.005) continue;
    const due = avstOpenItemDate(expense.dueDate);
    rows.push({
      id: expense.id,
      number: expense.supplierInvoiceNumber || expense.voucherNumber || expense.id,
      party: expense.supplierName || expense.description || 'Ukjent leverandør',
      date: avstOpenItemDate(expense.date),
      due,
      amount: remaining,
      bucket: avstOpenItemBucket(due, referenceDate),
      paymentStatus: summary?.status || '',
    });
  }
  return rows.sort((a, b) => String(a.due || '').localeCompare(String(b.due || '')));
};

// Salgsfakturaer med noe utestående. Samme form som leverandørsiden, slik at
// reskontroskjermen kan behandle dem likt.
const avstCustomerOpenItems = ({ referenceDate, state: suppliedState } = {}) => {
  const state = suppliedState || mergeState(runtime.appState);
  const rows = [];
  for (const invoice of safeArray(state.invoices)) {
    if (invoice?.status === 'draft' || invoice?.status === 'annulled' || ['offer', 'order_confirmation'].includes(invoice?.kind)) continue;
    const issued = avstOpenItemDate(invoice.date);
    if (issued && referenceDate && issued > referenceDate) continue;
    const summary = invoicePaymentSummary(state, invoice);
    const remaining = Math.round(num(summary.remainingAmount) * 100) / 100;
    if (Math.abs(remaining) < 0.005) continue;
    const due = avstOpenItemDate(invoice.dueDate);
    rows.push({
      id: invoice.id,
      number: invoice.invoiceNumber || invoice.id,
      party: invoice.customer?.name || invoice.customerName || 'Ukjent kunde',
      date: issued,
      due,
      amount: remaining,
      bucket: avstOpenItemBucket(due, referenceDate),
      paymentStatus: summary.status,
    });
  }
  return rows.sort((a, b) => String(a.due || '').localeCompare(String(b.due || '')));
};


// ------------------------------------------------------------------
// Låneregisteret
// ------------------------------------------------------------------
// Speiler `functions/accounting/businessLoanRegister.ts`. Lånene ligger i
// foretakets oppsett (`settings.loans`), som bankkontoene — et lån er en
// oppsettsfakta, ikke en transaksjon.
//
// Klassifiseringen kortsiktig/langsiktig KONKLUDERES IKKE her. Modellen regner
// ut hva lånets egen plan sier forfaller de neste tolv månedene, og lar byrået
// ta stillingen: avdragsfrihet, brudd på lånevilkår og refinansiering etter
// balansedagen er unntak en automatisk regel ikke ville sett.
const avstLoanNextTwelveMonths = (loan = {}, referenceDate = null) => {
  const type = String(loan.repaymentType || 'unknown');
  if (type === 'interest_only') return 0;
  if (type === 'unknown') return null;
  const installment = num(loan.installmentAmount);
  const remaining = Math.max(0, Math.round(num(loan.remainingTerms)));
  const perYear = Math.max(1, Math.round(num(loan.termsPerYear) || 12));
  if (!installment || !remaining) return null;
  let terms = Math.min(remaining, perYear);
  if (loan.interestOnlyUntil && referenceDate) {
    const monthsFree = Math.max(0, Math.round(
      (new Date(loan.interestOnlyUntil + 'T00:00:00') - new Date(referenceDate + 'T00:00:00')) / 2629800000,
    ));
    terms = Math.max(0, Math.min(terms, perYear - Math.round((monthsFree / 12) * perYear)));
  }
  return Math.round(installment * terms * 100) / 100;
};

const avstLoanFacts = ({ periodId, accountCodes = [], ledgerBalances = {}, state: suppliedState } = {}) => {
  const state = suppliedState || mergeState(runtime.appState);
  const range = window.avstLedgerPeriodRange ? window.avstLedgerPeriodRange(periodId) : null;
  const loans = safeArray(state.settings?.loans)
    .filter((loan) => loan && loan.id && loan.ledgerAccountCode);
  const rows = loans.map((loan) => {
    const booked = ledgerBalances[loan.ledgerAccountCode];
    const nextTwelve = avstLoanNextTwelveMonths(loan, range?.to || null);
    return {
      ...loan,
      bookedBalance: booked === undefined ? null : booked,
      nextTwelveMonths: nextTwelve,
      longTerm: nextTwelve === null || booked === undefined
        ? null
        : Math.round((Math.abs(num(booked)) - nextTwelve) * 100) / 100,
      needsPlan: nextTwelve === null,
    };
  });
  const registered = new Set(rows.map((row) => row.ledgerAccountCode));
  return {
    rows,
    // Gjeldskontoer uten et registrert lån. Det er nettopp dem avstemmingen skal
    // fange: en konto ingen har lagt en nedbetalingsplan for.
    unregistered: accountCodes.filter((code) => !registered.has(code) && Math.abs(num(ledgerBalances[code])) > 0.005),
  };
};

Object.assign(window, {
  buildLedgerLines, buildLedgerLinesByAccount, ledgerLineTotals, ledgerLineAgeBucket, ledgerLineIsoDate,
  avstVatFacts, avstPayrollFacts, avstPeriodMonths,
  avstSupplierOpenItems, avstCustomerOpenItems, avstOpenItemBucket, AVST_OPEN_ITEM_BUCKETS,
  avstLoanFacts, avstLoanNextTwelveMonths,
});
